// Typed API endpoint methods. Each method returns ApiResult<T> from client.ts.

import type {
  AuthMeResponse,
  DeleteUserResponse,
  GetUserResponse,
  HealthResponse,
  InviteUserRequest,
  InviteUserResponse,
  ListRolesResponse,
  ListUsersQuery,
  ListUsersResponse,
  PatchUserRequest,
  PatchUserResponse,
  ResendInviteResponse,
  ResetMfaResponse,
  RestoreUserResponse,
  SendPasswordResetResponse,
  SignOutAllResponse,
  CreateTenantRequest,
  CreateTenantResponse,
  DeleteTenantResponse,
  GetTenantResponse,
  ListTenantsQuery,
  ListTenantsResponse,
  PatchTenantRequest,
  PatchTenantResponse,
  ResolveTenantBySlugResponse,
  RestoreTenantResponse,
  TenantId,
  TenantStatusActionResponse,
  UserId,
  UserStatusActionResponse,
  AnonymizeTenantUserResponse,
  GetTenantUserResponse,
  InviteTenantUserRequest,
  InviteTenantUserResponse,
  ListTenantUsersQuery,
  ListTenantUsersResponse,
  PatchTenantUserRequest,
  PatchTenantUserResponse,
  ReactivateTenantUserResponse,
  RemoveTenantUserResponse,
  ResendTenantInviteResponse,
  SuspendTenantUserResponse,
  TenantUserId,
  CreateKBEntryRequest,
  CreateKBEntryResponse,
  DuplicateKBEntryResponse,
  DeleteKBEntryResponse,
  GetKBEntryResponse,
  KBEntryId,
  KBEntryLifecycleResponse,
  ListKBEntriesQuery,
  ListKBEntriesResponse,
  PatchKBEntryRequest,
  PatchKBEntryResponse,
  RestoreKBEntryResponse,
  ReviewExcerptRequest,
  ReviewExcerptResponse,
  SearchKBEntriesRequest,
  SearchKBEntriesResponse,
  SuggestQaPairVariantsRequest,
  SuggestQaPairVariantsResponse,
  ArchivePersonaResponse,
  CreatePersonaRequest,
  CreatePersonaResponse,
  CreatePersonaScenarioRequest,
  CreatePersonaScenarioResponse,
  GetPersonaResponse,
  GetPersonaScenarioMessagesResponse,
  ListPersonaScenariosResponse,
  ListPersonaeQuery,
  ListPersonaeResponse,
  PatchPersonaRequest,
  PatchPersonaResponse,
  PersonaConversationId,
  PersonaId,
  PostPersonaMessageFeedbackRequest,
  PostPersonaMessageFeedbackResponse,
  PostPersonaScenarioMessageRequest,
  PostPersonaScenarioMessageResponse,
  RenamePersonaScenarioRequest,
  RenamePersonaScenarioResponse,
  RestorePersonaResponse,
  CreateSourceDocumentRequest,
  CreateSourceDocumentResponse,
  DeleteSourceDocumentRequest,
  DeleteSourceDocumentResponse,
  GetSourceDocumentResponse,
  ListSourceDocumentsQuery,
  ListSourceDocumentsResponse,
  PatchSourceDocumentRequest,
  PatchSourceDocumentResponse,
  RestoreSourceDocumentResponse,
  SourceDocumentId,
  TriggerIngestionResponse,
  TriggerUploadResponse,
  ApproveBookStructureResponse,
  BookStructureId,
  GetBookStructureResponse,
  ListBookStructureAuditResponse,
  GetSourceMapResponse,
  PatchBookStructureRequest,
  PatchBookStructureResponse,
  ProposeMapResponse,
  RejectBookStructureRequest,
  RejectBookStructureResponse,
  CreateFrameworkRequest,
  CreateFrameworkResponse,
  FrameworkStatusActionResponse,
  GetFrameworkResponse,
  ListFrameworksQuery,
  ListFrameworksResponse,
  PatchFrameworkRequest,
  PatchFrameworkResponse,
  GetLabsTestRunResponse,
  LabsEnrichPreviewRequest,
  LabsEnrichPreviewResponse,
  LabsRunQueryRequest,
  LabsRunQueryResponse,
  ListLabsTestRunsResponse,
  ListSettingHistoryResponse,
  ListSettingsResponse,
  PatchSettingRequest,
  PatchSettingResponse,
} from '@gc-coach/shared';
import { apiFetch, type ApiResult } from './client.js';

export function getHealth(): Promise<ApiResult<HealthResponse>> {
  return apiFetch<HealthResponse>('/health', { auth: false });
}

/**
 * Public, unauthenticated. Posted from the stuck MFA challenge screen when
 * a user can't produce their TOTP code. Backend looks up super_admins and
 * emails them. The response is always 200 + a correlation requestId (no
 * enumeration leak); the UI shows a generic "request sent" confirmation.
 */
export function requestMfaRecovery(
  body: { email: string },
): Promise<ApiResult<{ requestId: string }>> {
  return apiFetch<{ requestId: string }>('/auth/mfa-recovery-request', {
    method: 'POST',
    body,
    auth: false,
  });
}

export function getMe(): Promise<ApiResult<AuthMeResponse>> {
  return apiFetch<AuthMeResponse>('/auth/me');
}

// ─── Users API ───────────────────────────────────────────────────────────────

export function listUsers(query: ListUsersQuery = {}): Promise<ApiResult<ListUsersResponse>> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.role) params.set('role', query.role);
  if (query.q) params.set('q', query.q);
  const qs = params.toString();
  return apiFetch<ListUsersResponse>(`/users${qs ? `?${qs}` : ''}`);
}

export function getUser(id: UserId): Promise<ApiResult<GetUserResponse>> {
  return apiFetch<GetUserResponse>(`/users/${encodeURIComponent(id)}`);
}

export function inviteUser(body: InviteUserRequest): Promise<ApiResult<InviteUserResponse>> {
  return apiFetch<InviteUserResponse>('/users/invite', { method: 'POST', body });
}

export function patchUser(
  id: UserId,
  body: PatchUserRequest,
): Promise<ApiResult<PatchUserResponse>> {
  return apiFetch<PatchUserResponse>(`/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

export function resendInvite(id: UserId): Promise<ApiResult<ResendInviteResponse>> {
  return apiFetch<ResendInviteResponse>(`/users/${encodeURIComponent(id)}/resend-invite`, {
    method: 'POST',
  });
}

export function suspendUser(id: UserId): Promise<ApiResult<UserStatusActionResponse>> {
  return apiFetch<UserStatusActionResponse>(`/users/${encodeURIComponent(id)}/suspend`, {
    method: 'POST',
  });
}

export function reactivateUser(id: UserId): Promise<ApiResult<UserStatusActionResponse>> {
  return apiFetch<UserStatusActionResponse>(`/users/${encodeURIComponent(id)}/reactivate`, {
    method: 'POST',
  });
}

export function resetMfa(id: UserId): Promise<ApiResult<ResetMfaResponse>> {
  return apiFetch<ResetMfaResponse>(`/users/${encodeURIComponent(id)}/reset-mfa`, {
    method: 'POST',
  });
}

export function sendPasswordReset(
  id: UserId,
): Promise<ApiResult<SendPasswordResetResponse>> {
  return apiFetch<SendPasswordResetResponse>(
    `/users/${encodeURIComponent(id)}/send-password-reset`,
    { method: 'POST' },
  );
}

export function signOutAll(id: UserId): Promise<ApiResult<SignOutAllResponse>> {
  return apiFetch<SignOutAllResponse>(`/users/${encodeURIComponent(id)}/sign-out-all`, {
    method: 'POST',
  });
}

export function deleteUser(
  id: UserId,
  reason?: string,
): Promise<ApiResult<DeleteUserResponse>> {
  return apiFetch<DeleteUserResponse>(`/users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    ...(reason ? { body: { reason } } : {}),
  });
}

/**
 * Restore a removed user. Super_admin-only at the API layer — UI should
 * hide / disable this for non-super_admin viewers.
 */
export function restoreUser(id: UserId): Promise<ApiResult<RestoreUserResponse>> {
  return apiFetch<RestoreUserResponse>(`/users/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
  });
}

export function listRoles(): Promise<ApiResult<ListRolesResponse>> {
  return apiFetch<ListRolesResponse>('/roles');
}

// ─── Tenants API ─────────────────────────────────────────────────────────────

export function listTenants(query: ListTenantsQuery = {}): Promise<ApiResult<ListTenantsResponse>> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.standing) params.set('standing', query.standing);
  if (query.jurisdiction) params.set('jurisdiction', query.jurisdiction);
  if (query.q) params.set('q', query.q);
  const qs = params.toString();
  return apiFetch<ListTenantsResponse>(`/tenants${qs ? `?${qs}` : ''}`);
}

export function getTenant(id: TenantId): Promise<ApiResult<GetTenantResponse>> {
  return apiFetch<GetTenantResponse>(`/tenants/${encodeURIComponent(id)}`);
}

export function resolveTenantBySlug(
  slug: string,
): Promise<ApiResult<ResolveTenantBySlugResponse>> {
  return apiFetch<ResolveTenantBySlugResponse>(`/tenants/by-slug/${encodeURIComponent(slug)}`);
}

export function createTenant(
  body: CreateTenantRequest,
): Promise<ApiResult<CreateTenantResponse>> {
  return apiFetch<CreateTenantResponse>('/tenants', { method: 'POST', body });
}

export function patchTenant(
  id: TenantId,
  body: PatchTenantRequest,
): Promise<ApiResult<PatchTenantResponse>> {
  return apiFetch<PatchTenantResponse>(`/tenants/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

export function suspendTenant(id: TenantId): Promise<ApiResult<TenantStatusActionResponse>> {
  return apiFetch<TenantStatusActionResponse>(`/tenants/${encodeURIComponent(id)}/suspend`, {
    method: 'POST',
  });
}

export function reactivateTenant(id: TenantId): Promise<ApiResult<TenantStatusActionResponse>> {
  return apiFetch<TenantStatusActionResponse>(`/tenants/${encodeURIComponent(id)}/reactivate`, {
    method: 'POST',
  });
}

export function deleteTenant(
  id: TenantId,
  reason?: string,
): Promise<ApiResult<DeleteTenantResponse>> {
  return apiFetch<DeleteTenantResponse>(`/tenants/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    ...(reason ? { body: { reason } } : {}),
  });
}

/** Restore a removed tenant. Super_admin-only at API + UI gates. */
export function restoreTenant(id: TenantId): Promise<ApiResult<RestoreTenantResponse>> {
  return apiFetch<RestoreTenantResponse>(`/tenants/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
  });
}

// ─── Tenant Users API ────────────────────────────────────────────────────────
// Manages consumer-pool users on behalf of a tenant. Same admin-pool JWT,
// same admin SPA — backend Lambda is `tenant-users`. See packages/api/src/
// handlers/tenant-users.ts for route shapes.

export function listTenantUsers(
  tenantId: TenantId,
  query: ListTenantUsersQuery = {},
): Promise<ApiResult<ListTenantUsersResponse>> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.q) params.set('q', query.q);
  const qs = params.toString();
  return apiFetch<ListTenantUsersResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/users${qs ? `?${qs}` : ''}`,
  );
}

export function getTenantUser(
  tenantId: TenantId,
  userId: TenantUserId,
): Promise<ApiResult<GetTenantUserResponse>> {
  return apiFetch<GetTenantUserResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`,
  );
}

export function inviteTenantUser(
  tenantId: TenantId,
  body: InviteTenantUserRequest,
): Promise<ApiResult<InviteTenantUserResponse>> {
  return apiFetch<InviteTenantUserResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/users`,
    { method: 'POST', body },
  );
}

export function patchTenantUser(
  tenantId: TenantId,
  userId: TenantUserId,
  body: PatchTenantUserRequest,
): Promise<ApiResult<PatchTenantUserResponse>> {
  return apiFetch<PatchTenantUserResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`,
    { method: 'PATCH', body },
  );
}

export function resendTenantInvite(
  tenantId: TenantId,
  userId: TenantUserId,
): Promise<ApiResult<ResendTenantInviteResponse>> {
  return apiFetch<ResendTenantInviteResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/resend-invite`,
    { method: 'POST' },
  );
}

export function suspendTenantUser(
  tenantId: TenantId,
  userId: TenantUserId,
): Promise<ApiResult<SuspendTenantUserResponse>> {
  return apiFetch<SuspendTenantUserResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/suspend`,
    { method: 'POST' },
  );
}

export function reactivateTenantUser(
  tenantId: TenantId,
  userId: TenantUserId,
): Promise<ApiResult<ReactivateTenantUserResponse>> {
  return apiFetch<ReactivateTenantUserResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/reactivate`,
    { method: 'POST' },
  );
}

export function removeTenantUser(
  tenantId: TenantId,
  userId: TenantUserId,
  reason?: string,
): Promise<ApiResult<RemoveTenantUserResponse>> {
  return apiFetch<RemoveTenantUserResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`,
    { method: 'DELETE', ...(reason ? { body: { reason } } : {}) },
  );
}

/**
 * Anonymize (GDPR erasure). Terminal — PII redacted, status='anonymized',
 * not restorable. Super_admin-only at API + UI gates.
 */
export function anonymizeTenantUser(
  tenantId: TenantId,
  userId: TenantUserId,
): Promise<ApiResult<AnonymizeTenantUserResponse>> {
  return apiFetch<AnonymizeTenantUserResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/anonymize`,
    { method: 'POST' },
  );
}

// ─── Frameworks API ──────────────────────────────────────────────────────────

export function listFrameworks(
  query: ListFrameworksQuery = {},
): Promise<ApiResult<ListFrameworksResponse>> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.domain) params.set('domain', query.domain);
  const qs = params.toString();
  return apiFetch<ListFrameworksResponse>(`/frameworks${qs ? `?${qs}` : ''}`);
}

export function getFramework(id: string): Promise<ApiResult<GetFrameworkResponse>> {
  return apiFetch<GetFrameworkResponse>(`/frameworks/${encodeURIComponent(id)}`);
}

export function createFramework(
  body: CreateFrameworkRequest,
): Promise<ApiResult<CreateFrameworkResponse>> {
  return apiFetch<CreateFrameworkResponse>('/frameworks', { method: 'POST', body });
}

export function patchFramework(
  id: string,
  body: PatchFrameworkRequest,
): Promise<ApiResult<PatchFrameworkResponse>> {
  return apiFetch<PatchFrameworkResponse>(`/frameworks/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

export function publishFramework(
  id: string,
): Promise<ApiResult<FrameworkStatusActionResponse>> {
  return apiFetch<FrameworkStatusActionResponse>(
    `/frameworks/${encodeURIComponent(id)}/publish`,
    { method: 'POST' },
  );
}

export function retireFramework(
  id: string,
): Promise<ApiResult<FrameworkStatusActionResponse>> {
  return apiFetch<FrameworkStatusActionResponse>(
    `/frameworks/${encodeURIComponent(id)}/retire`,
    { method: 'POST' },
  );
}

export function republishFramework(
  id: string,
): Promise<ApiResult<FrameworkStatusActionResponse>> {
  return apiFetch<FrameworkStatusActionResponse>(
    `/frameworks/${encodeURIComponent(id)}/republish`,
    { method: 'POST' },
  );
}

// ─── KB Entries API ──────────────────────────────────────────────────────────

export function listKBEntries(
  query: ListKBEntriesQuery = {},
): Promise<ApiResult<ListKBEntriesResponse>> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.type) params.set('type', query.type);
  if (query.domain) params.set('domain', query.domain);
  if (query.language) params.set('language', query.language);
  if (query.q) params.set('q', query.q);
  const qs = params.toString();
  return apiFetch<ListKBEntriesResponse>(`/kb-entries${qs ? `?${qs}` : ''}`);
}

export function getKBEntry(id: KBEntryId): Promise<ApiResult<GetKBEntryResponse>> {
  return apiFetch<GetKBEntryResponse>(`/kb-entries/${encodeURIComponent(id)}`);
}

export function createKBEntry(
  body: CreateKBEntryRequest,
): Promise<ApiResult<CreateKBEntryResponse>> {
  return apiFetch<CreateKBEntryResponse>('/kb-entries', { method: 'POST', body });
}

/** Duplicate a case-study KB entry (V1: case_study-only; backend
 *  returns 422 on other types). Returns the new entry as a fresh draft
 *  with "(copy) " prefix on body.archetype. */
export function duplicateKBEntry(
  id: KBEntryId,
): Promise<ApiResult<DuplicateKBEntryResponse>> {
  return apiFetch<DuplicateKBEntryResponse>(
    `/kb-entries/${encodeURIComponent(id)}/duplicate`,
    { method: 'POST' },
  );
}

export function patchKBEntry(
  id: KBEntryId,
  body: PatchKBEntryRequest,
): Promise<ApiResult<PatchKBEntryResponse>> {
  return apiFetch<PatchKBEntryResponse>(`/kb-entries/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

export function submitKBEntryForReview(
  id: KBEntryId,
): Promise<ApiResult<KBEntryLifecycleResponse>> {
  return apiFetch<KBEntryLifecycleResponse>(
    `/kb-entries/${encodeURIComponent(id)}/submit-review`,
    { method: 'POST' },
  );
}

export function publishKBEntry(id: KBEntryId): Promise<ApiResult<KBEntryLifecycleResponse>> {
  return apiFetch<KBEntryLifecycleResponse>(`/kb-entries/${encodeURIComponent(id)}/publish`, {
    method: 'POST',
  });
}

export function archiveKBEntry(id: KBEntryId): Promise<ApiResult<KBEntryLifecycleResponse>> {
  return apiFetch<KBEntryLifecycleResponse>(`/kb-entries/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
  });
}

export function unarchiveKBEntry(id: KBEntryId): Promise<ApiResult<KBEntryLifecycleResponse>> {
  return apiFetch<KBEntryLifecycleResponse>(`/kb-entries/${encodeURIComponent(id)}/unarchive`, {
    method: 'POST',
  });
}

export function deleteKBEntry(
  id: KBEntryId,
  reason?: string,
): Promise<ApiResult<DeleteKBEntryResponse>> {
  return apiFetch<DeleteKBEntryResponse>(`/kb-entries/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    ...(reason ? { body: { reason } } : {}),
  });
}

/** Restore a removed KB entry. Super_admin-only at API + UI gates. Entry returns to status='draft'. */
export function restoreKBEntry(id: KBEntryId): Promise<ApiResult<RestoreKBEntryResponse>> {
  return apiFetch<RestoreKBEntryResponse>(`/kb-entries/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
  });
}

/** Review action on a document_excerpt entry (approve / retag / reject). */
export function reviewExcerpt(
  id: KBEntryId,
  body: ReviewExcerptRequest,
): Promise<ApiResult<ReviewExcerptResponse>> {
  return apiFetch<ReviewExcerptResponse>(`/kb-entries/${encodeURIComponent(id)}/review-excerpt`, {
    method: 'POST',
    body,
  });
}

/**
 * Semantic search across KB entries. Embeds the query via Bedrock Titan v2 +
 * runs Atlas Vector Search. Returns ranked summaries with cosine scores.
 */
export function searchKBEntries(
  body: SearchKBEntriesRequest,
): Promise<ApiResult<SearchKBEntriesResponse>> {
  return apiFetch<SearchKBEntriesResponse>(`/kb-entries/search`, {
    method: 'POST',
    body,
  });
}

/**
 * AI-assisted question-variant suggestion for a qa_pair. Calls Bedrock Sonnet
 * 4.6 server-side; response is a batch of candidate paraphrases the editor
 * curates client-side (accept / edit-then-accept / reject) before saving.
 */
export function suggestKBQaPairVariants(
  id: KBEntryId,
  body: SuggestQaPairVariantsRequest,
): Promise<ApiResult<SuggestQaPairVariantsResponse>> {
  return apiFetch<SuggestQaPairVariantsResponse>(
    `/kb-entries/${encodeURIComponent(id)}/suggest-variants`,
    { method: 'POST', body },
  );
}

// ─── Source Documents ────────────────────────────────────────────────────

export function listSourceDocuments(
  query: ListSourceDocumentsQuery,
): Promise<ApiResult<ListSourceDocumentsResponse>> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.language) params.set('language', query.language);
  if (query.q?.trim()) params.set('q', query.q.trim());
  const qs = params.toString();
  return apiFetch<ListSourceDocumentsResponse>(
    `/source-documents${qs ? `?${qs}` : ''}`,
  );
}

export function getSourceDocument(
  id: SourceDocumentId,
): Promise<ApiResult<GetSourceDocumentResponse>> {
  return apiFetch<GetSourceDocumentResponse>(
    `/source-documents/${encodeURIComponent(id)}`,
  );
}

/**
 * Create the metadata record + receive a presigned S3 PUT URL.
 * The caller then PUTs the file to `upload_url` directly (not via the API).
 */
export function createSourceDocument(
  body: CreateSourceDocumentRequest,
): Promise<ApiResult<CreateSourceDocumentResponse>> {
  return apiFetch<CreateSourceDocumentResponse>(`/source-documents`, {
    method: 'POST',
    body,
  });
}

export function patchSourceDocument(
  id: SourceDocumentId,
  body: PatchSourceDocumentRequest,
): Promise<ApiResult<PatchSourceDocumentResponse>> {
  return apiFetch<PatchSourceDocumentResponse>(
    `/source-documents/${encodeURIComponent(id)}`,
    { method: 'PATCH', body },
  );
}

export function deleteSourceDocument(
  id: SourceDocumentId,
  reason?: string,
): Promise<ApiResult<DeleteSourceDocumentResponse>> {
  const body: DeleteSourceDocumentRequest = reason ? { reason } : {};
  return apiFetch<DeleteSourceDocumentResponse>(
    `/source-documents/${encodeURIComponent(id)}`,
    { method: 'DELETE', body },
  );
}

export function restoreSourceDocument(
  id: SourceDocumentId,
): Promise<ApiResult<RestoreSourceDocumentResponse>> {
  return apiFetch<RestoreSourceDocumentResponse>(
    `/source-documents/${encodeURIComponent(id)}/restore`,
    { method: 'POST' },
  );
}

/** Re-issue a presigned S3 PUT URL when the original 15-min window expired. */
export function triggerSourceDocumentUpload(
  id: SourceDocumentId,
): Promise<ApiResult<TriggerUploadResponse>> {
  return apiFetch<TriggerUploadResponse>(
    `/source-documents/${encodeURIComponent(id)}/trigger-upload`,
    { method: 'POST' },
  );
}

/**
 * Re-run ingestion on a document. V1: handler is a stub that flips status to
 * 'processing' — actual ingestion Lambda lands separately (Phase 2 build).
 */
export function triggerSourceDocumentIngestion(
  id: SourceDocumentId,
): Promise<ApiResult<TriggerIngestionResponse>> {
  return apiFetch<TriggerIngestionResponse>(
    `/source-documents/${encodeURIComponent(id)}/trigger-ingestion`,
    { method: 'POST' },
  );
}

// ─── Book structures (chapter + sub-section maps) ────────────────────────

/**
 * Kick off the Sonnet 4.6 proposer for a SourceDocument. Returns 202 with
 * a BookStructure in `proposing` state; the caller polls
 * `getBookStructure(id)` every 2-3 seconds until `proposing` flips to
 * false. Idempotent — re-calling on an already-proposed structure
 * returns the same one without re-spending Sonnet.
 */
export function proposeMap(
  sourceDocumentId: SourceDocumentId,
  opts: { force?: boolean } = {},
): Promise<ApiResult<ProposeMapResponse>> {
  const qs = opts.force ? '?force=true' : '';
  return apiFetch<ProposeMapResponse>(
    `/source-documents/${encodeURIComponent(sourceDocumentId)}/propose-map${qs}`,
    { method: 'POST' },
  );
}

export function getBookStructure(
  id: BookStructureId,
): Promise<ApiResult<GetBookStructureResponse>> {
  return apiFetch<GetBookStructureResponse>(
    `/book-structures/${encodeURIComponent(id)}`,
  );
}

/**
 * Fetches the RESOLVED source map for the two-pane review UI —
 * source_text + per-section char offsets + derived gaps in one shot.
 * Used exclusively by the new <MapReviewPage>; the propose-map polling
 * loop keeps using getBookStructure (the resolved shape includes an
 * S3 fetch + extraction cost we don't want to pay every 2s).
 */
export function getSourceMap(
  sourceDocumentId: string,
): Promise<ApiResult<GetSourceMapResponse>> {
  return apiFetch<GetSourceMapResponse>(
    `/source-documents/${encodeURIComponent(sourceDocumentId)}/map`,
  );
}

export function patchBookStructure(
  id: BookStructureId,
  body: PatchBookStructureRequest,
): Promise<ApiResult<PatchBookStructureResponse>> {
  return apiFetch<PatchBookStructureResponse>(
    `/book-structures/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export function approveBookStructure(
  id: BookStructureId,
): Promise<ApiResult<ApproveBookStructureResponse>> {
  return apiFetch<ApproveBookStructureResponse>(
    `/book-structures/${encodeURIComponent(id)}/approve`,
    { method: 'POST' },
  );
}

export function rejectBookStructure(
  id: BookStructureId,
  body: RejectBookStructureRequest = {},
): Promise<ApiResult<RejectBookStructureResponse>> {
  return apiFetch<RejectBookStructureResponse>(
    `/book-structures/${encodeURIComponent(id)}/reject`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/** MR 10 — fetches the per-structure mutation audit log, newest first.
 *  Powers the right-side history drawer. */
export function listBookStructureAudit(
  id: BookStructureId,
): Promise<ApiResult<ListBookStructureAuditResponse>> {
  return apiFetch<ListBookStructureAuditResponse>(
    `/book-structures/${encodeURIComponent(id)}/audit`,
  );
}

// ─── Personae ────────────────────────────────────────────────────────────

export function listPersonae(
  query: ListPersonaeQuery = {},
): Promise<ApiResult<ListPersonaeResponse>> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.q) params.set('q', query.q);
  const qs = params.toString();
  return apiFetch<ListPersonaeResponse>(`/personae${qs ? `?${qs}` : ''}`);
}

export function getPersona(id: PersonaId): Promise<ApiResult<GetPersonaResponse>> {
  return apiFetch<GetPersonaResponse>(`/personae/${encodeURIComponent(id)}`);
}

export function createPersona(
  body: CreatePersonaRequest,
): Promise<ApiResult<CreatePersonaResponse>> {
  return apiFetch<CreatePersonaResponse>('/personae', { method: 'POST', body });
}

export function patchPersona(
  id: PersonaId,
  body: PatchPersonaRequest,
): Promise<ApiResult<PatchPersonaResponse>> {
  return apiFetch<PatchPersonaResponse>(`/personae/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

export function archivePersona(
  id: PersonaId,
): Promise<ApiResult<ArchivePersonaResponse>> {
  return apiFetch<ArchivePersonaResponse>(
    `/personae/${encodeURIComponent(id)}/archive`,
    { method: 'POST' },
  );
}

export function restorePersona(
  id: PersonaId,
): Promise<ApiResult<RestorePersonaResponse>> {
  return apiFetch<RestorePersonaResponse>(
    `/personae/${encodeURIComponent(id)}/restore`,
    { method: 'POST' },
  );
}

export function listPersonaScenarios(
  id: PersonaId,
): Promise<ApiResult<ListPersonaScenariosResponse>> {
  return apiFetch<ListPersonaScenariosResponse>(
    `/personae/${encodeURIComponent(id)}/scenarios`,
  );
}

export function createPersonaScenario(
  id: PersonaId,
  body: CreatePersonaScenarioRequest = {},
): Promise<ApiResult<CreatePersonaScenarioResponse>> {
  return apiFetch<CreatePersonaScenarioResponse>(
    `/personae/${encodeURIComponent(id)}/scenarios`,
    { method: 'POST', body },
  );
}

export function renamePersonaScenario(
  personaId: PersonaId,
  scenarioId: PersonaConversationId,
  body: RenamePersonaScenarioRequest,
): Promise<ApiResult<RenamePersonaScenarioResponse>> {
  return apiFetch<RenamePersonaScenarioResponse>(
    `/personae/${encodeURIComponent(personaId)}/scenarios/${encodeURIComponent(scenarioId)}`,
    { method: 'PATCH', body },
  );
}

export function getPersonaScenarioMessages(
  personaId: PersonaId,
  scenarioId: PersonaConversationId,
): Promise<ApiResult<GetPersonaScenarioMessagesResponse>> {
  return apiFetch<GetPersonaScenarioMessagesResponse>(
    `/personae/${encodeURIComponent(personaId)}/scenarios/${encodeURIComponent(scenarioId)}/messages`,
  );
}

export function postPersonaScenarioMessage(
  personaId: PersonaId,
  scenarioId: PersonaConversationId,
  body: PostPersonaScenarioMessageRequest,
): Promise<ApiResult<PostPersonaScenarioMessageResponse>> {
  return apiFetch<PostPersonaScenarioMessageResponse>(
    `/personae/${encodeURIComponent(personaId)}/scenarios/${encodeURIComponent(scenarioId)}/messages`,
    { method: 'POST', body },
  );
}

export function postPersonaMessageFeedback(
  personaId: PersonaId,
  scenarioId: PersonaConversationId,
  messageId: string,
  body: PostPersonaMessageFeedbackRequest,
): Promise<ApiResult<PostPersonaMessageFeedbackResponse>> {
  return apiFetch<PostPersonaMessageFeedbackResponse>(
    `/personae/${encodeURIComponent(personaId)}/scenarios/${encodeURIComponent(scenarioId)}/messages/${encodeURIComponent(messageId)}/feedback`,
    { method: 'POST', body },
  );
}

// ─── GC Labs — RAG QA bench ────────────────────────────────────────────

export function runLabsQuery(
  body: LabsRunQueryRequest,
): Promise<ApiResult<LabsRunQueryResponse>> {
  return apiFetch<LabsRunQueryResponse>('/admin/labs/query', { method: 'POST', body });
}

export function previewLabsEnrichment(
  body: LabsEnrichPreviewRequest,
): Promise<ApiResult<LabsEnrichPreviewResponse>> {
  return apiFetch<LabsEnrichPreviewResponse>('/admin/labs/enrich-preview', {
    method: 'POST',
    body,
  });
}

export function listLabsTestRuns(
  limit?: number,
): Promise<ApiResult<ListLabsTestRunsResponse>> {
  const qs = limit ? `?limit=${limit}` : '';
  return apiFetch<ListLabsTestRunsResponse>(`/admin/labs/test-runs${qs}`);
}

export function getLabsTestRun(
  id: string,
): Promise<ApiResult<GetLabsTestRunResponse>> {
  return apiFetch<GetLabsTestRunResponse>(
    `/admin/labs/test-runs/${encodeURIComponent(id)}`,
  );
}

export function deleteLabsTestRun(
  id: string,
): Promise<ApiResult<{ deleted: true }>> {
  return apiFetch<{ deleted: true }>(
    `/admin/labs/test-runs/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

/**
 * Direct S3 PUT to the presigned URL. NOT via the API Lambda — the URL is
 * a one-shot direct-to-S3 upload. Content-Type + Content-Length must match
 * what was provided in the create request (server bound the URL to both).
 */
export async function uploadFileToPresignedUrl(
  uploadUrl: string,
  file: File,
): Promise<{ ok: true } | { ok: false; status: number; statusText: string }> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': file.type },
    body: file,
  });
  if (!response.ok) {
    return { ok: false, status: response.status, statusText: response.statusText };
  }
  return { ok: true };
}

// ─── Admin Settings — platform-wide product toggles ──────────────────────

export function listSettings(): Promise<ApiResult<ListSettingsResponse>> {
  return apiFetch<ListSettingsResponse>('/settings');
}

export function patchSetting(
  key: string,
  body: PatchSettingRequest,
): Promise<ApiResult<PatchSettingResponse>> {
  return apiFetch<PatchSettingResponse>(
    `/settings/${encodeURIComponent(key)}`,
    { method: 'PATCH', body },
  );
}

export function listSettingHistory(
  key: string,
): Promise<ApiResult<ListSettingHistoryResponse>> {
  return apiFetch<ListSettingHistoryResponse>(
    `/settings/${encodeURIComponent(key)}/history`,
  );
}
